import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { EMPTY, Observable, forkJoin } from 'rxjs';
import {
	GetProductsModel,
	Product,
	FeedbackModel,
	AgencyModel,
	ThresholdConfig,
} from 'src/app/interfaces/backend-models.interface';
import { ObjID } from 'src/app/interfaces/object-id.interface';
import { RuntimeDataEntry, RuntimeDataUpdate, RuntimeFilter } from 'src/app/interfaces/runtime-data.interface';
import { environment as env } from 'src/environments/environment';
import { LoadingErrorsService } from '../loading-errors/loading-errors.service';
import { ViewModel, WorkspaceDisableRule, WorkspaceDisableTimerInfo } from 'src/app/interfaces/views.interface';
import { TestWorkflowInfo, WorkflowData, WorkflowModel } from 'src/app/interfaces/workflow.interface';
import { SystemHealth } from 'src/app/interfaces/system-health.interface';
import { AclUserModel } from 'src/app/interfaces/user.interface';
import { AgencyWorkflowEnabled } from 'src/app/interfaces/agency.interface';
import { LatLonLocation, NodeType } from 'src/app/interfaces/node-models.interface';
import { AgreementsModel } from 'src/app/interfaces/privacy-model.interface';
import { OrcUserInfo } from 'src/app/interfaces/orc-user-info.interface';
import { ActivateOrchestrate, SelfRegistrationStatus } from 'src/app/interfaces/self-registration-models.interface';
import { cleanMetadata } from 'src/app/utils/metadata/metadata-functions.utils';
import {
	CountResponse,
	ReliabilityReportCount,
	ReliabilityReportTime,
	TriggerExecutionChartData,
} from 'src/app/interfaces/data-visualization.interface';

/**
 * Service for managing API calls while maintaining a clean interface for the rest of the application
 * Any functionality added here should also be added to api-offline.service.ts
 */
@Injectable({
	providedIn: 'root',
})
export class ApiService {
	constructor(
		private http: HttpClient,
		private loadingErrorsService: LoadingErrorsService
	) {}

	/** Workflows GET */
	public getWorkflows(): Observable<WorkflowModel[]> {
		return this.http.get<WorkflowModel[]>(`${env.manageApiUrl}/workflows`);
	}

	/** Sidetray GET */
	public getSidetray(): Observable<Product[]> {
		return this.http.get<GetProductsModel>(`${env.manageApiUrl}/products`).pipe(
			map(response =>
				response.products.map(p => ({
					// Display ACC as Unity Video, map metadata object into an array, and compute lastHeardFrom
					...p.product,
					name: p.product.id === 'ACC' ? 'Unity Video' : p.productFriendlyName,
					triggers: p.triggers.map(t => ({
						...t,
						metadataList: t.metadataList ?? [],
					})),
					actions: p.actions.map(a => ({
						...a,
						metadataList: a.metadataList ?? [],
					})),
					lastHeardFrom: p.triggers.length
						? new Date(response.currentTime).getTime() - new Date(p.product.lastEventTimestamp).getTime()
						: null,
				}))
			),
			map(products =>
				products
					// Remove the redundant trigger capability in UNITS if it has exactly 0 action and 1 trigger assuming its there by default
					.filter(
						p =>
							!(
								p.id === 'UNITS' &&
								p.actions.length === 0 &&
								p.triggers.length === 1 &&
								p.triggers.some(t => t.ecosystemId === 'emergency:unit')
							)
					)
					.map(p => ({
						/// Overwrite segmentId with SegmentName if present, and clean system properties and complex objects from metadata
						...p,
						triggers: p.triggers.map(t => ({
							...t,
							segmentId: t.segmentName ?? t.segmentId,
							metadataList: cleanMetadata(p.id, t.metadataList),
						})),
						actions: p.actions.map(a => ({
							...a,
							segmentId: a.segmentName ?? a.segmentId,
							metadataList: cleanMetadata(p.id, a.metadataList),
						})),
					}))
			)
		);
	}

	/**
	 * Initialize GET
	 */
	public getInitialize(): Observable<{ initialized: boolean }> {
		return this.http.get<{ initialized: boolean }>(`${env.manageApiUrl}/initialize`);
	}

	/** Release Notes GET */
	public getReleaseNotes(fileName: string): Observable<string> {
		return this.http.get(`./assets/release-notes/${fileName}`, { responseType: 'text' });
	}

	/** Runtime History GET */
	public getRuntimeHistory(filter: RuntimeFilter, ct?: string): Observable<RuntimeDataUpdate> {
		let params = new HttpParams();
		// Add continuationToken if one is provided
		if (ct) {
			params = params.append('ct', ct);
		}
		return this.http.post<RuntimeDataUpdate>(`${env.manageApiUrl}/workflow/history`, filter, { params });
	}

	/** Rename Node PUT */
	public putRenameNode(id: ObjID, type: NodeType, name: string) {
		const typeString = type === NodeType.outputOnly ? 'trigger' : 'action';
		return this.http.put(`${env.manageApiUrl}/capability/${typeString}/${id}/friendlyName`, { friendlyName: name });
	}

	public setTriggerIsCritical(triggerId: string, isCritical: boolean) {
		return this.http.put(`${env.manageApiUrl}/capability/trigger/${triggerId}/isCritical`, { isCritical });
	}

	public setTriggerStaticLocation(triggerId: string, location: LatLonLocation) {
		return this.http.put(`${env.manageApiUrl}/capability/trigger/${triggerId}/location`, location);
	}

	public deleteTriggerStaticLocation(triggerId: string) {
		return this.http.delete(`${env.manageApiUrl}/capability/trigger/${triggerId}/location`);
	}

	/** Delete workflow DELETE */
	public deleteWorkflow(workflow: WorkflowData) {
		return this.http.delete(`${env.manageApiUrl}/workflow/${workflow.id}`);
	}

	/** Workflow PUT */
	public putWorkflow(workflow: WorkflowData): Observable<WorkflowData> {
		return this.http.put<WorkflowData>(`${env.manageApiUrl}/workflow`, JSON.stringify(workflow));
	}

	/** Workflow status PUT */
	public putWorkflowStatus(workflow: WorkflowData) {
		return this.http.put<WorkflowData>(`${env.manageApiUrl}/workflow/${workflow.id}/status`, JSON.stringify(workflow));
	}

	/** testWorkflow */
	public testWorkflow(
		workflowId: string,
		triggerId: string,
		correlationId: string,
		connectionId: string
	): Observable<RuntimeDataEntry> {
		const body: TestWorkflowInfo = {
			connectionId,
			correlationId,
		};
		return this.http.post<RuntimeDataEntry>(
			`${env.manageApiUrl}/workflow/${workflowId}/trigger/${triggerId}/test`,
			body
		);
	}

	/** Workspace State PUT */
	public putWorkspaceState(state: boolean): Observable<AgencyModel> {
		const reqBody: AgencyWorkflowEnabled = {
			globalWorkflowEnabled: state,
		};
		return this.http.put<AgencyModel>(`${env.manageApiUrl}/agency/workflow-state`, JSON.stringify(reqBody));
	}

	/** Toggle workflow suppression */
	public toggleWorkflowSuppression(enabled: boolean): Observable<AgencyModel> {
		const reqBody: Partial<AgencyModel> = {
			suppressionDisabled: enabled,
		};
		return this.http.patch<AgencyModel>(`${env.manageApiUrl}/agency/suppression-disabled`, JSON.stringify(reqBody));
	}

	/** Save PPP Settings */
	public saveAgencyLocation(latitude: number, longitude: number): Observable<AgencyModel> {
		const reqBody = { latitude, longitude };
		return this.http.put<AgencyModel>(`${env.manageApiUrl}/agency/location`, JSON.stringify(reqBody));
	}

	/** Delete PPP Settings */
	public deleteAgencyLocation(): Observable<AgencyModel> {
		return this.http.delete<AgencyModel>(`${env.manageApiUrl}/agency/location`);
	}

	/** Toggle Aware Integration */
	public toggleAwareIntegration(enabled: boolean): Observable<AgencyModel> {
		const reqBody = {
			awareIntegrationEnabled: enabled,
		};
		return this.http.put<AgencyModel>(`${env.manageApiUrl}/agency/awareIntegrationEnabled`, JSON.stringify(reqBody));
	}

	/** Threshold config PUT */
	public putThresholdConfig(productId: ObjID, thresholdConfig: ThresholdConfig) {
		return this.http.put(`${env.manageApiUrl}/product/${productId}/threshold-config`, JSON.stringify(thresholdConfig));
	}

	/** Views GET */
	public getViews(): Observable<ViewModel[]> {
		return this.http.get<ViewModel[]>(`${env.manageApiUrl}/workspaces`);
	}

	/** View GET */
	public getView(workspaceId: string): Observable<ViewModel> {
		return this.http.get<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}`);
	}

	/** Get the WorkspaceDisableTimers for all provided workspaceIds */
	public getWorkspaceDisableTimers(workspaceIds: string[]): Observable<WorkspaceDisableTimerInfo[]> {
		return forkJoin(workspaceIds.map(workspaceId => this.getWorkspaceDisableTimer(workspaceId)));
	}

	/** Agency GET */
	public getAgency(): Observable<AgencyModel> {
		return this.http.get<AgencyModel>(`${env.manageApiUrl}/agency`).pipe(
			catchError(err => {
				this.loadingErrorsService.openErrorPage(
					`${err?.status?.toString()}`,
					err?.statusText,
					$localize`Failed to retrieve agency information.`
				);
				return EMPTY;
			})
		);
	}

	/** Views PUT */
	public putView(view: ViewModel) {
		return this.http.put<ViewModel>(`${env.manageApiUrl}/workspace`, view);
	}

	/** Views Enable ACL for workspace (view) */
	public enableAcl(workspaceId: ObjID) {
		return this.http.put<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}/acl`, {});
	}

	/** Views Disable ACL for workspace (view) */
	public disableAcl(workspaceId: ObjID) {
		return this.http.delete<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}/acl`);
	}

	/** Views DELETE */
	public deleteView(id: ObjID) {
		return this.http.delete(`${env.manageApiUrl}/workspace/${id}`);
	}

	/** POST feedback to CMSO */
	public postFeedback(feedbackType: string, title: string, details: string) {
		const reqBody: FeedbackModel = { feedbackType, title, details };
		return this.http.post(`${env.manageApiUrl}/feedback`, JSON.stringify(reqBody));
	}

	/** POST Error UI logs */
	public postErrorLog(body) {
		return this.http.post(`${env.manageApiUrl}/frontend/logs`, body);
	}

	/** System Health GET */
	public getSystemHealth(): Observable<SystemHealth[]> {
		return this.http.get<SystemHealth[]>(`${env.manageApiUrl}/incidents`);
	}

	/** Users GET */
	public getUsers(): Observable<AclUserModel[]> {
		return this.http.get<AclUserModel[]>(`${env.manageApiUrl}/users`);
	}

	/** User PUT */
	public addUser(user: AclUserModel, workspaceId: ObjID): Observable<ViewModel> {
		return this.http.put<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}/acl/${user.email}`, null);
	}

	/** User DELETE */
	public removeUser(user: AclUserModel, workspaceId: ObjID): Observable<ViewModel> {
		return this.http.delete<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}/acl/${user.email}`);
	}

	/** Get status about whether the tenant agency has Orchestrate, Central Station and if both are linked */
	public getSelfRegStatus(token: string, source: string): Observable<SelfRegistrationStatus> {
		const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', `Bearer ${token}`);
		let acsConnectorApiUrl: string;
		if (source === 'ACS AU' && env.acsConnectorAUApiUrl) {
			acsConnectorApiUrl = env.acsConnectorAUApiUrl;
		} else {
			acsConnectorApiUrl = env.acsConnectorApiUrl;
		}
		return this.http.get<SelfRegistrationStatus>(`${acsConnectorApiUrl}/v1/SelfRegistration/status`, {
			headers,
		});
	}

	public activateOrchestrate(token: string, activateOrchestrateBody: ActivateOrchestrate): Observable<any> {
		const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', `Bearer ${token}`);
		let acsConnectorApiUrl: string;
		if (activateOrchestrateBody.source === 'ACS AU' && env.acsConnectorAUApiUrl) {
			acsConnectorApiUrl = env.acsConnectorAUApiUrl;
		} else {
			acsConnectorApiUrl = env.acsConnectorApiUrl;
		}

		return this.http.post(`${acsConnectorApiUrl}/v1/SelfRegistration/orchestrate-activation`, activateOrchestrateBody, {
			headers,
		});
	}

	/** Patches user agreements to Orchestrate Manage based on the list of ids provided
	 * @param agreements Array of agreement IDs
	 * (eg.: msi_eula, msi_privacy_policy, pendo and twilio_sendgrid)
	 */
	public patchUserAgreementsOrc(agreements: string[]): Observable<OrcUserInfo> {
		return this.http.patch<OrcUserInfo>(`${env.manageApiUrl}/user/agreements`, agreements);
	}

	/** Get the agreements from Orchestrate (if user is not in MFE)  */
	public getAgreementsOrc(): Observable<AgreementsModel[]> {
		return this.http
			.get<AgreementsModel[]>(`${env.manageApiUrl}/agreements`)
			.pipe(map((agreementsFromGet: AgreementsModel[]) => this.consolidateAgreements(agreementsFromGet)));
	}

	/** Get the agreements from ACS connector (if user is in MFE)  */
	public getAgreementsACS(token: string, source: string): Observable<AgreementsModel[]> {
		const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', `Bearer ${token}`);
		let acsConnectorApiUrl: string;
		if (source === 'ACS AU' && env.acsConnectorAUApiUrl) {
			acsConnectorApiUrl = env.acsConnectorAUApiUrl;
		} else {
			acsConnectorApiUrl = env.acsConnectorApiUrl;
		}
		return this.http
			.get<AgreementsModel[]>(`${acsConnectorApiUrl}/v1/SelfRegistration/agreements`, { headers })
			.pipe(map((agreementsFromGet: AgreementsModel[]) => this.consolidateAgreements(agreementsFromGet)));
	}

	/** Common function to convert the get agreements response into the correct interface model */
	public consolidateAgreements(agreements: AgreementsModel[]): AgreementsModel[] {
		return agreements.map(agreement => ({ ...agreement, checked: false }));
	}

	/** Get the Orchestrate user info (CoreUser) */
	public getOrcUserInfo(): Observable<OrcUserInfo> {
		return this.http.get<OrcUserInfo>(`${env.manageApiUrl}/user`);
	}

	/** Make request to CC Admin to change current agency */
	public switchAgency(agencyId: string, token: string) {
		const options = {
			headers: new HttpHeaders().append('Authorization', `Bearer ${token}`),
			params: new HttpParams(),
		};
		return this.http.patch(`${env.admin2ApiUrl}/current-agency?agency=${agencyId}`, null, options);
	}

	/** Get info about the presently running WorkspaceDisableTimer if one exists */
	public getWorkspaceDisableTimer(workspaceId: string) {
		return this.http.get<WorkspaceDisableTimerInfo>(`${env.manageApiUrl}/workspace/${workspaceId}/disable-timer`);
	}

	/** Edit the rule defining which trigger will prompt a workspace to be disabled for what duration */
	public editWorkspaceDisableRule(workspaceId: string, disableRule: WorkspaceDisableRule) {
		return this.http.put<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}/disable-rule`, disableRule);
	}

	/** Delete the rule defining which trigger will prompt a workspace to be disabled for what duration */
	public deleteWorkspaceDisableRule(workspaceId: string) {
		return this.http.delete<ViewModel>(`${env.manageApiUrl}/workspace/${workspaceId}/disable-rule`);
	}

	/** Cancel an existing WorkspaceDisableTimer if one exists */
	public cancelWorkspaceDisableTimer(workspaceId: string) {
		return this.http.delete<{ canceled: boolean }>(`${env.manageApiUrl}/workspace/${workspaceId}/disable-timer`);
	}

	/** Adjust an existing WorkspaceDisableTimer if one exists */
	public updateWorkspaceDisableTimer(workspaceId: string, minutes: number) {
		return this.http.post<{ updated: boolean }>(`${env.manageApiUrl}/workspace/${workspaceId}/disable-timer/update`, {
			delta: minutes,
		});
	}

	/** Get Workflow Execution Chart Data*/
	public getWorkflowExecutionData(filter: RuntimeFilter): Observable<CountResponse> {
		return this.http.post<CountResponse>(`${env.manageApiUrl}/workflow/counts`, {
			ids: filter.workflowIds,
			dateRangeStart: filter.dateRangeStart,
			dateRangeEnd: filter.dateRangeEnd,
			timezoneOffset: new Date().getTimezoneOffset(),
		});
	}

	/** Get Action Activity Chart Data*/
	public getActionActivityData(filter: RuntimeFilter): Observable<CountResponse> {
		return this.http.post<CountResponse>(`${env.manageApiUrl}/action/counts`, {
			ids: filter.actionIds,
			dateRangeStart: filter.dateRangeStart,
			dateRangeEnd: filter.dateRangeEnd,
			timezoneOffset: new Date().getTimezoneOffset(),
		});
	}

	/** Get Incident count data for bar chart */
	public getIncidentCountData(productIds: string[], filter: RuntimeFilter): Observable<ReliabilityReportCount> {
		return this.http.post<ReliabilityReportCount>(`${env.manageApiUrl}/incident/counts`, {
			ids: productIds,
			dateRangeStart: filter.dateRangeStart,
			dateRangeEnd: filter.dateRangeEnd,
			timezoneOffset: new Date().getTimezoneOffset(),
		});
	}

	/** Get Incident times data for doughnut chart */
	public getIncidentTimeData(productIds: string[], filter: RuntimeFilter): Observable<ReliabilityReportTime> {
		return this.http.post<ReliabilityReportTime>(`${env.manageApiUrl}/incident/times`, {
			ids: productIds,
			dateRangeStart: filter.dateRangeStart,
			dateRangeEnd: filter.dateRangeEnd,
			timezoneOffset: new Date().getTimezoneOffset(),
		});
	}

	/** Get Trigger Execution Chart Data*/
	public getTriggerExecutionData(triggerIds: string[], filter: RuntimeFilter): Observable<TriggerExecutionChartData> {
		return this.http.post<TriggerExecutionChartData>(`${env.manageApiUrl}/trigger-event/counts`, {
			ids: triggerIds,
			dateRangeStart: filter.dateRangeStart,
			dateRangeEnd: filter.dateRangeEnd,
			timezoneOffset: new Date().getTimezoneOffset(),
		});
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
